import {
  Database,
  HardDrive,
  Wifi,
  ChevronRight,
  ChevronLeft,
  ArrowDown,
  ArrowUp,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useMemo, useEffect } from "react";
import { getSystemBattery } from "../services";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import { ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

function Healthstatus() {
  const selectedDistrict = useDistrict();
  const location = useLocation();
  const defaultTab = location.state?.tab || "dataStatus";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme, colors } = useTheme();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const hideAccountName = selectedDistrict?.selectedDistrict === "FB151";

  const [pageSize, setPageSize] = useState(window.innerWidth >= 1921 ? 20 : 9);

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state?.tab]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getSystemBattery(); // same service
        setApiData(res?.data?.data);
      } catch (err) {
        console.error("Error fetching system battery:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDistrict]);

  const filterBySearch = (rows) => {
    if (!search.trim()) return rows;

    const keyword = search.toLowerCase();

    return rows.filter((row) =>
      Object.values(row).some((value) => {
        if (!value) return false;

        const text = String(value).toLowerCase();

        return text.includes(keyword);
      }),
    );
  };
  // pagination
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1921) {
        setPageSize(20);
      } else {
        setPageSize(9);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // end pagination

  // ✅ MAP DATA STATUS (outlier_data)
  const dataStatusData = useMemo(() => {
    if (!apiData?.outlier_data) return [];
    return apiData.outlier_data.map((item) => ({
      accountId: item.account_id,
      category: item.name,
      accountName: item.overided_name,
      meterId: item.meter_id,
      register: item.register_number,
      avgFlow: Math.round(item.avg),
      count: item.include_count,
    }));
  }, [apiData]);

  // ✅ MAP DEVICE STATUS
  const deviceStatusData = useMemo(() => {
    if (!apiData?.device_data) return [];
    return apiData.device_data.map((item) => ({
      accountId: item.account_id,
      category: item.name,
      accountName: item.overided_name,
      meterId: item.meter_id,
      previousWeek: `${item.pre_week_range}\n${item.pre_flow}`,
      currentWeek: `${item.week_range}\n${item.flow}`,
      previousWeekFlow: Number(item.pre_flow) || 0,
      currentWeekFlow: Number(item.flow) || 0,
      previousWeekDate: item.pre_week_range,
      currentWeekDate: item.week_range,
      register: item.register_number,
      message:
        item.bad_tamper_msg || item.battery_status_msg || item.signal_msg || "",
    }));
  }, [apiData]);

  // ✅ MAP COMMUNICATION STATUS
  const communicationData = useMemo(() => {
    if (!apiData?.communication_data) return [];
    return apiData.communication_data.map((item) => ({
      accountId: item.account_id,
      category: item.name,
      accountName: item.overided_name,
      meterId: item.meter_id,
      previousWeek: `${item.pre_week_range}\n${item.pre_flow}`,
      currentWeek: `${item.week_range}\n${item.flow}`,
      previousWeekFlow: Number(item.pre_flow) || 0,
      currentWeekFlow: Number(item.flow) || 0,
      previousWeekDate: item.pre_week_range,
      currentWeekDate: item.week_range,
      register: item.register_number,
      message: item.last_comm_msg,
    }));
  }, [apiData]);

  // ✅ SWITCH DATA BASED ON TAB
  const currentFullData = useMemo(() => {
    if (activeTab === "dataStatus") return dataStatusData;
    if (activeTab === "deviceStatus") return deviceStatusData;
    return communicationData;
  }, [activeTab, dataStatusData, deviceStatusData, communicationData]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const filteredData = useMemo(() => {
    let data = filterBySearch(currentFullData);

    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined || bValue === undefined) return 0;

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        return sortConfig.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    return data;
  }, [currentFullData, search, sortConfig]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearch(""); // clear search on tab change
  };

  if (loading) {
    return (
      <div className="text-white p-6 h-full flex items-center justify-center">
        Loading system health data...
      </div>
    );
  }

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };
  return (
    <>
      {/* HEADER */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1
          className="text-4xl font-bold text-transparent bg-clip-text"
          style={{ backgroundImage: colors.gradient }}
        >
          System Health Overview
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
          Real-time monitoring and diagnostics
        </p>
      </motion.div>

      {/* TABS */}
      <div className="flex justify-between flex-wrap">
        <div className="flex gap-3 mb-3 flex-wrap">
          {[
            { id: "dataStatus", label: "Data Status", icon: Database },
            { id: "deviceStatus", label: "Device Status", icon: HardDrive },
            {
              id: "communicationStatus",
              label: "Communication Status",
              icon: Wifi,
            },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all"
                style={{
                  backgroundColor:
                    activeTab === tab.id
                      ? `${colors.primary}15`
                      : theme === "white"
                        ? "#ffffff"
                        : `${colors.primary}08`,
                  border: `1px solid ${
                    activeTab === tab.id ? colors.primary : colors.border
                  }`,
                  color:
                    activeTab === tab.id ? colors.text : colors.textSecondary,
                }}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-end mb-0 p-4">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-2 rounded-lg w-72 outline-none"
            style={{
              backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}
            onFocus={(e) =>
              (e.target.style.border = `1px solid ${colors.primary}`)
            }
            onBlur={(e) =>
              (e.target.style.border = `1px solid ${colors.border}`)
            }
          />

          {/* <div className="text-xs text-[#BDB9B5]">
              {filteredData.length} records found
            </div> */}
        </div>
      </div>

      {/* TABLE */}
      <div
        className="rounded-xl overflow-hidden mb-5"
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.cardBg,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                backgroundColor:
                  theme === "white" ? "#f8f9fa" : `${colors.primary}08`,
                borderTop: `1px solid ${colors.border}`,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <tr>
                <th
                  onClick={() => handleSort("accountId")}
                  className="px-6 py-4 text-left text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-4">
                    Account ID
                    {sortConfig.key === "accountId" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>
                <th
                  onClick={() => handleSort("category")}
                  className="px-6 py-4 text-left text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-4">
                    Category
                    {sortConfig.key === "category" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>
                {!hideAccountName && (
                  <th
                    onClick={() => handleSort("accountName")}
                    className="px-6 py-4 text-left text-sm cursor-pointer"
                    style={{ color: colors.textSecondary }}
                  >
                    <span className="flex items-center gap-4">
                      Account Name
                      {sortConfig.key === "accountName" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUp
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        ) : (
                          <ArrowDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )}
                    </span>
                  </th>
                )}
                <th
                  onClick={() => handleSort("meterId")}
                  className="px-6 py-4 text-left text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-4">
                    Meter ID
                    {sortConfig.key === "meterId" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {activeTab === "dataStatus" ? (
                  <>
                    <th
                      onClick={() => handleSort("register")}
                      className="px-6 py-4 text-left text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Register
                        {sortConfig.key === "register" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("count")}
                      className="px-6 py-4 text-left text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Count
                        {sortConfig.key === "count" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("avgFlow")}
                      className="px-6 py-4 text-left text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Avg Flow
                        {sortConfig.key === "avgFlow" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                  </>
                ) : (
                  <>
                    <th
                      onClick={() => handleSort("previousWeekFlow")}
                      className="px-6 py-4 text-left text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Previous Week
                        {sortConfig.key === "previousWeekFlow" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("currentWeekFlow")}
                      className="px-6 py-4 text-left text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Current Week
                        {sortConfig.key === "currentWeekFlow" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("message")}
                      className="px-6 py-4 text-left text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      <span className="flex items-center gap-4">
                        Message
                        {sortConfig.key === "message" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </span>
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b transition-all duration-200 cursor-pointer" // Added cursor-pointer
                  style={{
                    borderColor: colors.border,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = `${colors.primary}08`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                  // 2. Add the onClick handler
                  onClick={() => {
                    // Calculate 30 days back from today
                    const thirtyDaysBack = dayjs()
                      .subtract(30, "day")
                      .format("YYYY-MM-DD");

                    navigate(
                      `/meter-leak-details?category=ALL&meter_id=${row.meterId}&time_range=Current&selected_time=${thirtyDaysBack}`,
                      {
                        state: {
                          // Passing these as null/undefined will trigger the
                          // hiding logic we implemented in the detail page
                          water_loss_gallons: null,
                          price: null,
                          leak_rate: null,
                        },
                      },
                    );
                  }}
                >
                  {/* Account ID */}
                  <td
                    className="px-6 py-4 text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.accountId}
                  </td>

                  {/* Category */}
                  <td
                    className="px-6 py-4 text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {row.category}
                  </td>
                  {!hideAccountName && (
                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {row.accountName}
                    </td>
                  )}
                  {/* Meter ID */}
                  <td className="px-6 py-4 text-sm">
                    <span
                      className="font-bold underline decoration-1 underline-offset-4"
                      style={{ color: colors.primary }}
                    >
                      {row.meterId}
                    </span>
                  </td>

                  {activeTab === "dataStatus" ? (
                    <>
                      {/* Register */}
                      <td
                        className="px-6 py-4 text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        {row.register || "-"}
                      </td>

                      {/* Count */}
                      <td
                        className="px-6 py-4 text-sm"
                        style={{ color: colors.text }}
                      >
                        {row.count}
                      </td>

                      {/* Avg Flow */}
                      <td
                        className="px-6 py-4 text-sm font-semibold"
                        style={{ color: colors.text }}
                      >
                        {row.avgFlow}
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Previous Week */}
                      <td
                        className="px-6 py-4 text-sm whitespace-pre-line"
                        style={{ color: colors.textSecondary }}
                      >
                        {row.previousWeek}
                      </td>

                      {/* Current Week */}
                      <td
                        className="px-6 py-4 text-sm whitespace-pre-line"
                        style={{ color: colors.textSecondary }}
                      >
                        {row.currentWeek}
                      </td>

                      {/* Message */}
                      <td
                        className="px-6 py-4 text-sm"
                        style={{
                          color: colors.warning || "#FF9800",
                        }}
                      >
                        {row.message}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* PAGINATION */}
          {totalPages > 0 && (
            <div
              className="flex items-center justify-between p-4 border-t"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.cardBg,
              }}
            >
              {/* Entries Info */}
              <div className="text-xs" style={{ color: colors.textSecondary }}>
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + pageSize, filteredData.length)} of{" "}
                {filteredData.length} records
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {/* Prev Button */}
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="p-1.5 rounded-md border transition-all"
                    style={{
                      borderColor:
                        currentPage === 1 ? colors.border : colors.primary,
                      color:
                        currentPage === 1
                          ? colors.textSecondary
                          : colors.primary,
                      opacity: currentPage === 1 ? 0.3 : 1,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {/* Page Selector Dropdown */}
                  <div className="flex items-center gap-2 px-2">
                    <span
                      className="text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      Page
                    </span>
                    <select
                      value={currentPage}
                      onChange={(e) => setCurrentPage(Number(e.target.value))}
                      className="text-sm rounded-md px-2 py-1 outline-none cursor-pointer"
                      style={{
                        backgroundColor: colors.cardBg,
                        borderColor: colors.border,
                        borderWidth: "1px",
                        color: colors.text,
                      }}
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <option key={page} value={page}>
                            {page}
                          </option>
                        ),
                      )}
                    </select>
                    <span
                      className="text-sm max-[576px]:w-[60px] max-[576px]:text-center"
                      style={{ color: colors.textSecondary }}
                    >
                      of {totalPages}
                    </span>
                  </div>

                  {/* Next Button */}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    className="p-1.5 rounded-md border transition-all"
                    style={{
                      borderColor:
                        currentPage === totalPages
                          ? colors.border
                          : colors.primary,
                      color:
                        currentPage === totalPages
                          ? colors.textSecondary
                          : colors.primary,
                      opacity: currentPage === totalPages ? 0.3 : 1,
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <div
                  className="flex items-center gap-2 text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  <span>Rows:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when size changes
                    }}
                    className="rounded px-2 py-1 text-sm border outline-none cursor-pointer"
                    style={{
                      background:
                        theme === "white" ? "#ffffff" : colors.background,
                      borderColor: colors.border,
                      color: colors.text,
                    }}
                  >
                    <option value={9}>9</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* End PAGINATION */}
        </div>
      </div>
    </>
  );
}

export default Healthstatus;
